import { useConfigInputs, useConfigActions, useMetrics, useVendorCtx, useCalcStatus } from 'shared/state/configContexts';
import { useAbility, PERM } from 'shared/access';
import type { SectionStatus } from 'shared/state/sectionStatusContext';
import { storageSetSummary, type StorageVariant } from './storageSetSummary';
import {
  resolveSsdOptions, resolveJbodAndHdd, resolveEncoding,
  resolveVariantBasics, resolveVariantOptions, resolveSizingSummary, resolveNodeModel, resolveMlec,
  type Mapping,
} from './storageSetOptions';
import { useStorageSetHandlers } from './useStorageSetHandlers';

// Re-exported so a consumer (the Task 7 view) can keep importing Mapping from
// this module, unchanged, even though it is now defined in storageSetOptions.
export type { Mapping };

/** One hook for both storage sets. `variant` picks the side. R14 split the
 *  field/option resolution into pure functions in storageSetOptions.ts and
 *  every on* handler into useStorageSetHandlers.ts — this module only
 *  composes them and returns the brief's exact shape, so it stays under the
 *  max-lines-per-function / complexity budgets the single-function version
 *  used to blow. The sizing MODE (node count vs raw capacity) is system-wide
 *  and has its own hook and row (useSizingMode / SizingModeToggle); this hook only
 *  reads it, to pick which of the two sizing fields a set shows. */
export function useStorageSetSection(variant: StorageVariant) {
  const { config } = useConfigInputs();
  const { updateConfig } = useConfigActions();
  const metrics = useMetrics();
  const { vendor } = useVendorCtx();
  const { isCalculating } = useCalcStatus();
  const ability = useAbility();

  // vendor.capabilities is plain untyped JS (src/config/vendorRegistry.js);
  // this cast is the boundary where it enters typed code. Every other
  // Record<string, any> for `caps`/`pMap` in this file and in
  // storageSetOptions.ts is the same shape flowing through — see above.
  const caps = vendor.capabilities as Record<string, any>;
  const pMap = caps.productMappings ?? {};
  const filterCtx = { hasSpecialAccess: ability.can(PERM.ADMIN_SKUS), quarter: config.quarter };

  const { isExpansion, enabled, nodeCount, minNodes } = resolveVariantBasics(variant, config);
  const { ssdOptions, currentSsd } = resolveSsdOptions(variant, pMap, filterCtx, config);
  const { jbodOptions, hddOptions, currentHdd } = resolveJbodAndHdd(caps, pMap, filterCtx, config);
  const { encodingOptions, encodingValue, constraintNote } = resolveEncoding(variant, config, enabled, nodeCount);
  const { sparesOptions, sparesValue, compressionOptions, compressionValue, e2e } =
    resolveVariantOptions(variant, config, caps);
  const { minRawPb, sizeBy, showAdvanced } = resolveSizingSummary(variant, config, isExpansion);
  const { showMlec, mlecOn, mlecLocked, mlecReason, mlecLoss } = resolveMlec(variant, config, vendor.id);
  const { showNodeModel, nodeModelValue, nodeModelOptions, nodeModelLocked, nodeModelReason } =
    resolveNodeModel(variant, config, ability.can(PERM.FLASH_DUAL_CPU));
  // A disabled set is 'none', not 'ok' — a green "done" dot next to a set the
  // rep has switched off would be misleading.
  const status: SectionStatus = !enabled ? 'none' : (nodeCount <= 0 || constraintNote !== null) ? 'warn' : 'ok';

  const handlers = useStorageSetHandlers({
    variant, config, updateConfig, ssdOptions, hddOptions, minNodes, enabled, e2e,
  });

  return {
    enabled, onToggleEnabled: handlers.onToggleEnabled, isExpansion,
    sizeBy,
    nodeCount, minNodes, onNodeCountChange: handlers.onNodeCountChange,
    minRawPb, onMinRawPbChange: handlers.onMinRawPbChange,
    ssdOptions, ssdValue: currentSsd?.partNumber ?? '', onSsdChange: handlers.onSsdChange,
    showNodeModel, nodeModelValue, nodeModelOptions, nodeModelLocked, nodeModelReason,
    onNodeModelChange: handlers.onNodeModelChange,
    jbodOptions, jbodValue: String(config.jbodSize ?? ''), onJbodChange: handlers.onJbodChange,
    hddOptions, hddValue: currentHdd?.partNumber ?? '', onHddChange: handlers.onHddChange,
    encodingOptions, encodingValue, onEncodingChange: handlers.onEncodingChange,
    sparesOptions, sparesValue, onSparesChange: handlers.onSparesChange,
    compressionOptions, compressionValue, onCompressionChange: handlers.onCompressionChange, compressionLocked: e2e,
    e2e, onE2eToggle: handlers.onE2eToggle,
    showAdvanced,
    showMlec, mlecOn, mlecLocked, mlecReason, mlecLoss, onMlecChange: handlers.onMlecChange,
    constraintNote,
    summary: storageSetSummary(variant, config, metrics),
    status,
    isCalculating,
  };
}
